/**
 * WebSocket client singleton for Backoffice.
 * Enables live real-time streams (like Developer Audit Logs).
 */

const FATAL_CLOSE_CODES = new Set<number>([4401, 4002, 1000]);

const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 30000];

function backoffDelay(attempt: number): number {
  const idx = Math.min(attempt, BACKOFF_MS.length - 1);
  const base = BACKOFF_MS[idx] ?? BACKOFF_MS[BACKOFF_MS.length - 1] ?? 1000;
  const jitter = (Math.random() * 0.4 - 0.2) * base;
  return Math.max(500, base + jitter);
}

const MIRROR_WS_ORIGINS: Record<string, string> = {
  "collectandtrack.lol": "wss://ws.collectandtrack.lol/ws",
  "collectandtrack.xyz": "wss://ws.collectandtrack.xyz/ws",
  "internal-cat.top": "wss://ws.internal-cat.top/ws",
};

export function buildWsUrl(): string {
  const explicit = import.meta.env.VITE_WS_URL as string | undefined;
  if (explicit) return explicit;
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;

    // Local development routes to backend default port 3000
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "ws://localhost:3000/ws";
    }

    const root = hostname.split(".").slice(-2).join(".");
    const mirrored = MIRROR_WS_ORIGINS[root];
    if (mirrored) return mirrored;

    if (hostname.endsWith(".pages.dev")) {
      return "wss://ws.collectandtrack.lol/ws";
    }
  }
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
}

export interface ServerEnvelope {
  type: string;
  id?: string;
  ts?: string;
  data?: any;
  room?: string;
  code?: string;
  sessionId?: string;
  bootId?: string;
  serverTime?: string;
}

type EventHandler = (event: ServerEnvelope) => void;
type CloseHandler = (code: number, reason: string) => void;

interface SocketState {
  ws: WebSocket | null;
  reconnectAttempts: number;
  reconnectTimer: number | null;
  pendingFrames: string[];
  closedDeliberately: boolean;
  handlers: Set<EventHandler>;
  closeHandlers: Set<CloseHandler>;
  joinedRooms: Set<string>;
}

const state: SocketState = {
  ws: null,
  reconnectAttempts: 0,
  reconnectTimer: null,
  pendingFrames: [],
  closedDeliberately: false,
  handlers: new Set(),
  closeHandlers: new Set(),
  joinedRooms: new Set(),
};

function send(payload: unknown): void {
  const frame = JSON.stringify(payload);
  const ws = state.ws;
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(frame);
    return;
  }
  state.pendingFrames.push(frame);
}

function flushPending(): void {
  const ws = state.ws;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  for (const f of state.pendingFrames) ws.send(f);
  state.pendingFrames = [];
}

function rejoinRooms(): void {
  for (const room of state.joinedRooms) {
    send({ type: "join", room });
  }
}

function scheduleReconnect(): void {
  if (state.closedDeliberately) return;
  if (state.reconnectTimer !== null) return;
  const delay = backoffDelay(state.reconnectAttempts);
  state.reconnectAttempts += 1;
  state.reconnectTimer = window.setTimeout(() => {
    state.reconnectTimer = null;
    openSocket();
  }, delay);
}

function openSocket(): void {
  if (state.ws && (state.ws.readyState === WebSocket.OPEN || state.ws.readyState === WebSocket.CONNECTING)) {
    return;
  }
  let ws: WebSocket;
  try {
    ws = new WebSocket(buildWsUrl());
  } catch {
    scheduleReconnect();
    return;
  }
  state.ws = ws;

  ws.onopen = () => {
    state.reconnectAttempts = 0;
    flushPending();
    rejoinRooms();
  };

  ws.onmessage = (ev) => {
    let envelope: ServerEnvelope;
    try {
      envelope = JSON.parse(typeof ev.data === "string" ? ev.data : "") as ServerEnvelope;
    } catch {
      return;
    }
    for (const h of state.handlers) {
      try {
        h(envelope);
      } catch {
        /* ignored */
      }
    }
  };

  ws.onclose = (ev) => {
    state.ws = null;
    for (const h of state.closeHandlers) {
      try {
        h(ev.code, ev.reason);
      } catch {
        /* ignored */
      }
    }
    if (FATAL_CLOSE_CODES.has(ev.code)) {
      state.closedDeliberately = true;
      return;
    }
    scheduleReconnect();
  };

  ws.onerror = () => {
    /* browser close will trigger */
  };
}

export function connect(): void {
  state.closedDeliberately = false;
  openSocket();
}

export function disconnect(): void {
  state.closedDeliberately = true;
  if (state.reconnectTimer !== null) {
    window.clearTimeout(state.reconnectTimer);
    state.reconnectTimer = null;
  }
  state.joinedRooms.clear();
  state.pendingFrames = [];
  if (state.ws) {
    try {
      state.ws.close(1000, "client_disconnect");
    } catch {
      /* ignored */
    }
    state.ws = null;
  }
}

export function isConnected(): boolean {
  return state.ws?.readyState === WebSocket.OPEN;
}

export function subscribe(handler: EventHandler): () => void {
  state.handlers.add(handler);
  return () => {
    state.handlers.delete(handler);
  };
}

export function onClose(handler: CloseHandler): () => void {
  state.closeHandlers.add(handler);
  return () => {
    state.closeHandlers.delete(handler);
  };
}

export function joinRoom(room: string): void {
  state.joinedRooms.add(room);
  send({ type: "join", room });
}

export function leaveRoom(room: string): void {
  state.joinedRooms.delete(room);
  send({ type: "leave", room });
}
